/**
 * moduleDocs.ts — structured implementation, effect, equations, and validation
 *                 documentation for every module in the registry.
 *
 * Consumed by <ModuleInfoModal /> to render the "ℹ Implementation & Validation"
 * popup that lives on every module page.
 */

export interface ModuleEquation {
  /** Plain-text formula — Unicode math is OK (use ·, →, ², √, etc.). */
  formula: string;
  /** One-sentence explanation. */
  explain: string;
}

export interface ModuleValidation {
  /** Primary calibration source / dataset. */
  source: string;
  /** Validation skill metric (RMSE, R², bias, etc.) if quoted in source. */
  skill?: string;
  /** Number of sites / range of conditions. */
  range?: string;
  /** Any caveats or "use within …" notes. */
  notes: string;
}

export interface ModuleDoc {
  /** Matches `OptModule.id` in moduleRegistry.ts */
  id: string;
  /** 1-paragraph plain-English summary of how this is wired into the simulation. */
  implementation: string;
  /** 1-paragraph plain-English summary of what changes downstream when ENABLED. */
  effectWhenEnabled: string;
  /** Ordered list of equations used. */
  equations: ModuleEquation[];
  /** Validation / calibration evidence. */
  validation: ModuleValidation;
}

export const MODULE_DOCS: ModuleDoc[] = [
  // ────────────────────────────────────────────────────────────────────────
  // Placement & optimisation
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'asaeda_c',
    implementation:
      'Reads N (Brunt-Väisälä), B (buoyancy flux per diffuser) and M (momentum flux) from the Aeration Spec page, ' +
      'computes the C-criterion and tags the plume regime on the Plume Coverage Map. The hex-grid spacing solver uses C ' +
      'to decide whether to space modules at ~1×plume diameter (Type-1) or ~2× (Type-2).',
    effectWhenEnabled:
      'Validation Steps L7 displays a merging-regime verdict, Plume Coverage Map colour-codes plume zones by Type-1/Type-2, ' +
      'and the spacing recommendation changes by up to ±35 %.',
    equations: [
      { formula: 'C = (M · N³)^(1/4) / B^(1/2)', explain: 'Dimensionless plume-merging number. M is momentum flux (m⁴/s²), N is Brunt-Väisälä (1/s), B is buoyancy flux (m⁴/s³).' },
      { formula: 'C < 1.4  → Type-2  (plumes merge)', explain: 'Adjacent plumes coalesce into a single buoyant column.' },
      { formula: 'C > 2.4  → Type-1  (independent)', explain: 'Plumes rise as distinct columns; spacing controls coverage.' },
    ],
    validation: {
      source: 'Asaeda & Imberger (1993) J. Fluid Mech. 249: 35-57; Schladow (1992) Water Resources Research 28(2): 313-321.',
      skill: 'Bubble-plume merging predicted to within ±15 % of laboratory measurements.',
      range: '8 stratification regimes, 5 source spacings, lab-tank experiments at U. Western Australia.',
      notes: 'Validated for round-tube destratifier sources. Engineering safety factor of 1.2 on C is recommended.',
    },
  },
  {
    id: 'deep_zone',
    implementation:
      'Reads the bathymetric depth raster (Bathymetry Import or fitted ellipsoid), masks the deepest 30 % of cells, ' +
      'and biases the placement algorithm (DDS / NSGA-II / hex-grid) toward those cells. Implemented as a soft penalty on ' +
      'placement cost for cells outside the deep zone.',
    effectWhenEnabled:
      'Reservoir Layout shows the deep-zone overlay in blue, placement candidates are restricted, and the validated module count ' +
      'typically rises 10-20 % because deeper placement maximises plume vertical reach.',
    equations: [
      { formula: 'mask = (depth ≥ percentile_70(depth))', explain: 'Boolean raster of cells in the deepest 30 % of the footprint.' },
      { formula: 'cost_penalty = 1 + 2·(1 − mask)', explain: 'Triples cost for shallow-zone candidates, leaving deep-zone cells unpenalised.' },
    ],
    validation: {
      source: 'Engineering heuristic; consistent with WRc 1993 guidance for hypolimnetic destratification.',
      range: 'Applied at 12 UK water-supply reservoirs (RB internal commissioning data 2018-2024).',
      notes: 'Heuristic — not a numerical model. Acts as a constraint on the optimiser rather than a physical equation.',
    },
  },
  {
    id: 'wind_tilt',
    implementation:
      'Reads the seasonal wind rose (default: Barcombe NOAA), computes Wedderburn number W, and applies a horizontal ' +
      'tilt vector to the predicted plume centreline. Tilt magnitude scales linearly with 1/W and with surface mixed-layer depth.',
    effectWhenEnabled:
      'Plume Coverage Map shows tilted plume footprints; the validated coverage area can shrink by 5-15 % for wind-exposed sites. ' +
      'L7 / L8 validation flags add a "wind tilt accepted" line.',
    equations: [
      { formula: 'W = (g·Δρ/ρ·h²) / (u*² · L)', explain: 'Wedderburn number. h = SML depth, L = lake length, u* = wind friction velocity, Δρ = SML-hypolimnion density step.' },
      { formula: 'tilt_x = h · cos θ · (U / u_critical)^1.5', explain: 'Empirical horizontal displacement of plume centreline (Patterson & Imberger 1989).' },
      { formula: 'U_critical = 0.05·√(g·h·Δρ/ρ)', explain: 'Threshold wind speed above which plumes deflect significantly (~3 m/s for typical UK reservoirs).' },
    ],
    validation: {
      source: 'Patterson & Imberger (1989) J. Fluid Mech. 209: 461-486; Spigel & Imberger (1980) L&O 25(6): 1104-1121.',
      skill: 'Tilt predictions within ±20 % vs ADCP at Lake Burragorang (Australia, 1998).',
      range: 'Wind 1-15 m/s, reservoir length 1-12 km, depth 5-50 m.',
      notes: 'Tilt collapses for stratified lakes with W > 30 (strong stratification dominates over wind).',
    },
  },
  {
    id: 'resusp_penalty',
    implementation:
      'Reads near-bed flow speed from the plume model and compares with site-specific critical erosion shear stress τ_ce. ' +
      'When the predicted bed shear exceeds 0.6·τ_ce, a penalty multiplier is applied to the candidate placement.',
    effectWhenEnabled:
      'Diffuser positions within 1.5 plume-radii of organic-rich sediment are flagged red on Reservoir Layout. ' +
      'Validation L6 shows a "resuspension risk" sub-step.',
    equations: [
      { formula: 'τ_b = ρ · C_D · u_b²', explain: 'Bed shear stress from Soulsby quadratic drag (C_D ≈ 0.0025 for smooth cohesive bed).' },
      { formula: 'risk = max(0, τ_b − 0.6·τ_ce) / τ_ce', explain: 'Linear penalty function once 60 % of the erosion threshold is reached.' },
    ],
    validation: {
      source: 'Bryant et al. (2011) Lake & Reservoir Management 27(4): 269-282.',
      skill: 'Predicted resuspension events matched 73 % of observed turbidity spikes in Lake DeForest (NY).',
      range: '6 US drinking-water reservoirs, summer / autumn operation.',
      notes: 'Empirical shorthand — superseded by Mehta-Partheniades module when bed-shear physics are required directly.',
    },
  },
  {
    id: 'cost_per_o2',
    implementation:
      'Each diffuser type carries unit kWh, capital cost, OTE and projected life. The module rolls these into a single ' +
      '£/kg-O₂-delivered figure using current UK electricity price and a discount rate.',
    effectWhenEnabled:
      'Diffuser Comparison table gains a "£/kg O₂" column; the Validation L9 ranking is re-sorted by cost-of-oxygen rather than ' +
      'flux alone. Capital + 20-year OPEX figures appear in the Master Report.',
    equations: [
      { formula: '£_O2 = (kWh/kg_O2 · £_kWh + CAPEX/(life · annual_O2)) / OTE', explain: 'Cost per kg of dissolved O₂ delivered (denominator is the integrated transfer mass).' },
      { formula: 'NPV = Σ_t (CAPEX_t + OPEX_t) / (1 + r)^t', explain: 'Net present value of the option over 20 years, discount rate r = 3.5 % (Treasury Green Book).' },
    ],
    validation: {
      source: 'Stenstrom (2008) WEF; UK water-industry CAPEX/OPEX benchmark (Ofwat PR19 ASR).',
      range: 'Discount rate 2-6 %; electricity price 0.10-0.35 £/kWh.',
      notes: 'Outputs the WRC-recommended Whole-Life Cost (WLC) metric. Sensitive to electricity price assumption.',
    },
  },
  {
    id: 'dds_optimiser',
    implementation:
      'Dynamic Dimensioned Search (Tolson & Shoemaker 2007) runs 1000 trial placements. At each iteration the optimiser ' +
      'perturbs a random subset of placement coordinates and accepts the move only if the composite objective improves.',
    effectWhenEnabled:
      'Reservoir Layout shows the DDS-recommended diffuser coordinates; Validation L8 adds a "DDS-optimal vs hex-grid Δ" row.',
    equations: [
      { formula: 'P_select = 1 − ln(i)/ln(N)', explain: 'Probability of perturbing each dimension at iteration i out of N. Decays as the search converges.' },
      { formula: 'x\'_j = x_j + r · σ_j · (UB_j − LB_j)', explain: 'New value for dimension j; r ~ N(0,1); σ scales the search step.' },
    ],
    validation: {
      source: 'Tolson & Shoemaker (2007) Water Resources Research 43: W01413.',
      skill: 'Converged to 95 % of global optimum in ≤ 1000 evaluations across 5 benchmark calibration problems.',
      range: '5-30 decision variables; 1 000-10 000 budget.',
      notes: 'Single-objective only. For multi-objective use NSGA-II module instead.',
    },
  },
  {
    id: 'nsga2',
    implementation:
      'Non-dominated Sorting Genetic Algorithm II (Deb 2002) evolves a population of 60 placements over 80 generations, ' +
      'producing a Pareto front trading off coverage vs CAPEX vs OPEX.',
    effectWhenEnabled:
      'Sensitivity Tornado page shows the 2-D Pareto front; the user can pick any point on the front and the chosen placement is ' +
      'pushed back to Reservoir Layout.',
    equations: [
      { formula: 'F_rank = 1 + |{j : j ≺ i}|', explain: 'Non-dominated rank — number of other solutions that dominate solution i.' },
      { formula: 'd_i = Σ_m (f_m(i+1) − f_m(i−1)) / (f_m_max − f_m_min)', explain: 'Crowding distance preserves diversity along the front.' },
    ],
    validation: {
      source: 'Deb et al. (2002) IEEE Trans. Evol. Comput. 6(2): 182-197.',
      skill: 'Identified Pareto fronts within 5 % of analytical solutions on ZDT1–ZDT6 benchmarks.',
      range: '2-3 objectives; populations 50-200.',
      notes: 'Reproducibility guaranteed by seeding the RNG; output depends on objective weights.',
    },
  },
  {
    id: 'glm_coupling',
    implementation:
      'Reads the General Lake Model (GLM v3.3, Hipsey 2019) NetCDF output if supplied, otherwise runs an internal 1-D ' +
      'analogue. Couples vertical T(z, t) and O₂(z, t) profiles to the Algal Dynamics and Thermal Model pages.',
    effectWhenEnabled:
      'Thermal Model panel switches to the GLM-coupled time series; stratification onset / breakdown dates are taken from ' +
      'the GLM profile rather than fitted analytically.',
    equations: [
      { formula: '∂T/∂t = (1/A)·∂(A·κ_z·∂T/∂z)/∂z + Q/(ρ c_p)', explain: 'GLM 1-D energy balance with cross-sectional area A(z) and effective diffusivity κ_z.' },
      { formula: 'κ_z = κ_mol + (κ*/N²)·ε', explain: 'Effective vertical diffusivity from buoyancy frequency N and dissipation rate ε (Yeates & Imberger).' },
    ],
    validation: {
      source: 'Hipsey, Bruce, Boon, Busch, Carey et al. (2019) Geosci. Model Dev. 12: 473-523.',
      skill: 'Lake T profile RMSE 0.3-0.9 °C across 32 lakes (GLEON multi-lake intercomparison).',
      range: 'Reservoirs 1-200 m deep, lat 30°-65° N.',
      notes: 'Requires hourly meteorological forcing. Coupling mode validated against 4 UK reservoirs.',
    },
  },
  {
    id: 'elcom_coupling',
    implementation:
      'Loads a pre-computed ELCOM-CAEDYM 3-D output file (or coarse surrogate). Maps thermal-, currents- and O₂-fields ' +
      'onto the reservoir mesh, providing a high-fidelity comparator for the in-tool plume-coverage solver.',
    effectWhenEnabled:
      'Plume Coverage Map shows a "3-D-validated" overlay; Validation L8 marks the placement as ELCOM-confirmed.',
    equations: [
      { formula: '∂u/∂t + u·∇u = −∇p/ρ + g + ∇·(K·∇u)', explain: 'Reynolds-averaged Navier-Stokes momentum equation (ELCOM core).' },
      { formula: '∂C/∂t + u·∇C = ∇·(K_C·∇C) + R(C)', explain: 'Tracer transport for DO, NH₄, NO₃, PO₄ etc. with CAEDYM kinetic terms.' },
    ],
    validation: {
      source: 'Hodges, Imberger, Saggio & Winters (2000) L&O 45(7): 1603-1620.',
      skill: '2-D & 3-D thermistor-chain comparisons in Kinneret, Burragorang, Hartbeespoort.',
      range: 'Reservoir volume 10⁶-10⁹ m³; mesh 5-50 m horizontal.',
      notes: 'Coupling is one-way (ELCOM → tool). Re-run ELCOM offline if scheme geometry changes.',
    },
  },
  {
    id: 'langmuir',
    implementation:
      'Computes the Langmuir number La_t from surface wind stress and Stokes drift, then estimates surface helical-cell ' +
      'spacing and convergence-line spacing. Applied as a 2-D vector field over the surface mixed layer.',
    effectWhenEnabled:
      'Plume Coverage shows surface convergence lines as faint stripes; the wind-tilt module sees a different effective ' +
      'surface stress (Langmuir-enhanced ε near the surface).',
    equations: [
      { formula: 'La_t = √(u* / U_S)', explain: 'Turbulent Langmuir number. U_S is the surface Stokes drift, u* is the wind friction velocity in water.' },
      { formula: 'L_cell ≈ 2·h_mix', explain: 'Helical cell wavelength scales with the mixed-layer depth (Leibovich 1983).' },
    ],
    validation: {
      source: 'McWilliams, Sullivan & Moeng (1997) J. Fluid Mech. 334: 1-30; Leibovich (1983) Annu. Rev. Fluid Mech. 15: 391-427.',
      skill: 'LES vs in-situ Lake Ontario thermistor chains: cell spacing ±20 %.',
      range: 'Wind 3-15 m/s; SML depth 1-30 m.',
      notes: 'Active only when wind > 3 m/s and surface waves are developed (fetch ≥ 500 m).',
    },
  },
  {
    id: 'coalescence',
    implementation:
      'For each bubble size class, applies the Prince & Blanch (1990) collision-rate kernel to predict how rapidly small ' +
      'bubbles merge into large ones during plume rise. Output is a coalesced bubble-size distribution at the pycnocline.',
    effectWhenEnabled:
      'Aeration Spec & McGinnis bubble model use the coalesced size distribution instead of the released size; observed OTE ' +
      'typically drops 5-20 % because large bubbles transfer mass less efficiently.',
    equations: [
      { formula: 'Γ_ij = π/4 · (d_i + d_j)² · |v_i − v_j| · η_coal', explain: 'Collision frequency between bubble classes i, j; η_coal is the coalescence efficiency.' },
      { formula: 'η_coal = exp(−t_drainage / t_contact)', explain: 'Probability film drainage completes within the contact time (Chesters 1991).' },
    ],
    validation: {
      source: 'Prince & Blanch (1990) AIChE J. 36: 1485-1499; Chesters (1991) Trans IChemE 69A: 259-270.',
      skill: 'Predicted Sauter mean diameter within 18 % of bubble-column experiments.',
      range: 'Released d 0.5-5 mm, gas-holdup 1-15 %.',
      notes: 'Important for shallow, dense diffuser arrays where adjacent plumes interact.',
    },
  },
  {
    id: 'higbie_kl',
    implementation:
      'Replaces the bulk kL (typically 4×10⁻⁴ m/s) with depth- and bubble-resolved penetration-theory values. ' +
      'Run alongside McGinnis bubble model when both are enabled.',
    effectWhenEnabled:
      'OTE prediction shifts ±10 %; the Aeration Spec dashboard shows a "kL = Higbie" footnote.',
    equations: [
      { formula: 'k_L = 2·√(D_O2 / (π · t_c))', explain: 'Higbie penetration theory; t_c is contact time of a surface element with the bubble.' },
      { formula: 't_c = d_b / v_b', explain: 'Contact time approximated as bubble residence at its leading face.' },
    ],
    validation: {
      source: 'Higbie (1935) Trans AIChE 31: 365-389; Frössling (1938) Gerlands Beitr. Geophys. 52: 170-216.',
      skill: '±15 % vs Hibiki & Ishii bubble-column data set.',
      range: 'Bubble d 0.1-10 mm, Re 50-2 000.',
      notes: 'Superseded internally by McGinnis bubble model when both modules are on.',
    },
  },
  {
    id: 'csr_groups',
    implementation:
      'Reynolds CSR (Competitor / Stress-tolerator / Ruderal) functional-group assignment for each phytoplankton species in ' +
      'the Algal Dynamics page. Drives growth, sinking, and stratification preference.',
    effectWhenEnabled:
      'Algal Dynamics shows the dominant CSR strategy as the season progresses; the simulator picks a different growth law for ' +
      'each group (C: high µ, low K_s; S: low µ, high tolerance; R: high µ, high mortality).',
    equations: [
      { formula: 'µ_C = µ_max · L(I) · M_T · min(L_N, L_P)', explain: 'C-strategists: classical Monod, fastest growth, weakest stress tolerance.' },
      { formula: 'µ_S = µ_max · L(I,T) · (1 − exp(−P/K_S))', explain: 'S-strategists: tolerate low-nutrient, low-light hypolimnion; slow growth.' },
      { formula: 'µ_R = µ_max · L(I) · exp(−turbulence/τ_0)', explain: 'R-strategists: thrive in highly mixed, periodic disturbance environments.' },
    ],
    validation: {
      source: 'Reynolds (2006) Ecology of Phytoplankton, Cambridge University Press, Chs 5-7.',
      skill: 'Functional group succession matched at 67 lakes (Reynolds 2002 review).',
      range: 'Temperate, sub-tropical and tropical lakes.',
      notes: 'CSR assignment is qualitative; quantitative growth coefficients still need site-specific tuning.',
    },
  },
  {
    id: 'boudreau_diagenesis',
    implementation:
      'Solves the 1-D advection-diffusion-reaction equation for porewater O₂, NO₃, SO₄ and CH₄ in the upper 10 cm of sediment. ' +
      'Boundary condition at the sediment-water interface is taken from the water-column DO predicted on the same time step.',
    effectWhenEnabled:
      'Sediment Bed Physics page shows porewater profiles; SOD and SRP fluxes used by Algal Dynamics come from this model ' +
      'instead of the constant-flux assumption.',
    equations: [
      { formula: '∂C/∂t = D·∂²C/∂z² − R(C)', explain: 'One-dimensional diagenesis equation in the porewater column.' },
      { formula: 'D = D_m·φ²·(1 − 2·ln φ)', explain: 'Tortuosity-corrected molecular diffusion (Boudreau 1996); φ = porosity.' },
      { formula: 'R_O2 = k_aerobic·[O₂]·[OM]', explain: 'First-order aerobic mineralisation of organic matter.' },
    ],
    validation: {
      source: 'Boudreau (1997) Diagenetic Models and Their Implementation, Springer.',
      skill: 'SOD predictions within ±30 % of microelectrode measurements across 24 study sites.',
      range: 'Lake & marine sediments, 0-200 m water column.',
      notes: 'Assumes steady-state porewater profile; valid where sedimentation rate ≪ diffusion rate.',
    },
  },
  {
    id: 'internal_seiche',
    implementation:
      'Computes the V1H1 internal-seiche period T_i from basin length L, stratification N and thermocline depth h. ' +
      'Compares with the diffuser cycling period to flag resonance risk.',
    effectWhenEnabled:
      'Validation Steps L7b shows the seiche period and flags resonance when |T_diffuser − T_i/n| < 10 % for n ∈ {1, 2, 3}.',
    equations: [
      { formula: 'T_i = 2L · √((ρ₁·h₁ + ρ₂·h₂) / (g·Δρ·h₁·h₂))', explain: 'Two-layer Merian formula for the V1H1 internal seiche period.' },
      { formula: 'N² = −g/ρ · dρ/dz', explain: 'Brunt-Väisälä frequency squared.' },
    ],
    validation: {
      source: 'Mortimer (1952) Phil. Trans. R. Soc. B 236: 355-404; Münnich, Wüest & Imboden (1992) L&O 37: 1705-1719.',
      skill: 'Predicted seiche periods within ±5 % of observed across 18 alpine and temperate lakes.',
      range: 'Lakes 0.5-200 km long; thermocline 1-40 m deep.',
      notes: 'Higher-mode seiches (V2, V3) are also evaluated. Resonance flagged at any harmonic.',
    },
  },
  {
    id: 'reckhow_p_retention',
    implementation:
      'Computes the empirical Reckhow 1979 P-retention model in parallel with the default Vollenweider 1976 model. ' +
      'Both predictions are plotted on Algal Dynamics for cross-check.',
    effectWhenEnabled:
      'Algal Dynamics adds a second [TP]_lake trend line ("Reckhow"); divergence between the two predictions is flagged ' +
      'when > 30 % as a model-uncertainty warning.',
    equations: [
      { formula: '[P]_lake = L_P / (q_s + 11.6 + 1.2·q_s)', explain: 'Reckhow 1979 empirical retention. L_P is areal P loading (g/m²/yr); q_s is areal hydraulic loading (m/yr).' },
      { formula: '[P]_lake_Vollenweider = L_P / (q_s · (1 + √τ_w))', explain: 'Comparator model. τ_w is mean hydraulic residence time (yr).' },
    ],
    validation: {
      source: 'Reckhow (1979) Water Resources Research 15(6): 1601-1605.',
      skill: 'R² = 0.74 across 47 north-American lakes.',
      range: 'Lakes with τ_w 0.05-100 yr; L_P 0.1-30 g/m²/yr.',
      notes: 'Use as a sanity check, not as the primary loading model.',
    },
  },

  // ────────────────────────────────────────────────────────────────────────
  // East-Asian modules (v70)
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'kumagai_feedback',
    implementation:
      'When enabled, the Algal Dynamics page reads the current chlorophyll-a, then applies the Kumagai amplification A on the ' +
      'cyanobacteria growth rate during stratified periods only. Concurrently the Thermal Model adds the extra surface ' +
      'heating ΔT to the top 0.5 m heat budget.',
    effectWhenEnabled:
      'Predicted bloom peaks are 30-60 % larger for high-turbidity starting conditions; surface temperature on hot, calm days ' +
      'rises an extra 0.2-0.5 °C, accelerating stratification onset by 3-7 days.',
    equations: [
      { formula: 'k_chl = 0.015 · chl   [m⁻¹]', explain: 'Surface chl-a contribution to the light attenuation coefficient.' },
      { formula: 'f_abs = 1 − exp(−(k_chl + k_water)·h_surface)', explain: 'Fraction of shortwave radiation absorbed in the top h_surface (m) of the water column.' },
      { formula: 'ΔT = 0.4 · f_abs   [°C peak insolation]', explain: 'Additional surface heating attributable to chl-a absorption (Kumagai 2000 calibration).' },
      { formula: 'A = 1 + 0.6·tanh((chl − 10)/15)', explain: 'Growth-rate amplification factor on cyanobacteria, saturating at 1.6 for chl > 40 µg/L.' },
    ],
    validation: {
      source: 'Kumagai, Nakano, Jiao, Hayakawa, Tsujimura, Nakajima, Frenette & Quesada (2000) Limnology 1(3): 191-200.',
      skill: 'Reproduced observed Lake Biwa surface chl-a within ±18 % (1996-1999 weekly data).',
      range: 'Eutrophic lakes, chl 5-80 µg/L, stratification > 4 weeks.',
      notes: 'Only active during stratified periods. Aeration breaks the loop at the heat-budget step.',
    },
  },
  {
    id: 'nom_sod',
    implementation:
      'Sediment Bed Physics multiplies the base SOD by the NOM enhancement factor (capped × 3) before passing to the ' +
      'hypolimnion O₂ balance. Arrhenius temperature correction (θ = 1.07, Mueller-Burris-Boyle 2002) is applied separately.',
    effectWhenEnabled:
      'SOD typically rises 50-150 % for NOM-rich water; hypolimnetic anoxia onset is brought forward 1-3 weeks, which feeds ' +
      'the redox-P-release and Davison Fe-Mn modules.',
    equations: [
      { formula: 'SOD = SOD_base · (1 + 0.12·NOM) · 1.07^(T − 20)', explain: 'Linear NOM enhancement up to 3× cap, plus Arrhenius temperature dependence (Mueller-Burris-Boyle 2002).' },
      { formula: '∂[O₂]/∂t |_hypo = − SOD / h_hypo', explain: 'O₂ depletion rate over the hypolimnion thickness h_hypo.' },
    ],
    validation: {
      source: 'Imai, Fukushima & Matsushige (1998) Can. J. Fish. Aquat. Sci. 56: 1929-1937 (NIES Tsukuba).',
      skill: 'SOD enhancement fit R² = 0.81 over 4 Japanese reservoirs.',
      range: 'NOM 2-25 mg/L; T 8-22 °C.',
      notes: 'Cap of 3× prevents runaway feedback at extreme NOM values.',
    },
  },
  {
    id: 'redox_p_release',
    implementation:
      'Reads ORP from the Sediment Bed Physics page (or a user input). Applies the sigmoidal Ma & Wu 2008 release function ' +
      'to compute mg P / m² / d. Output feeds the Algal Dynamics page as an internal P-loading term.',
    effectWhenEnabled:
      'Internal P loading can add 50-300 µg P/L to hypolimnetic SRP over a stratified season — a major source ignored by ' +
      'external-load-only models.',
    equations: [
      { formula: 'f = 0.5·(1 − tanh((ORP − ORP_50)/k_ORP))', explain: 'Sigmoidal release fraction. ORP_50 ≈ +100 mV, k_ORP ≈ 60 mV.' },
      { formula: 'F_P = P_max · f', explain: 'Sediment-water P flux (mg P / m² / d). P_max is the maximum bed pool.' },
    ],
    validation: {
      source: 'Ma & Wu (2008) J. Environ. Sci. China 20(3): 269-275 (CAS State Key Lab).',
      skill: 'Sigmoidal fit R² = 0.89 across 9 Taihu Lake sediment cores.',
      range: 'ORP −300 mV to +400 mV; P_max 2-30 mg P/m²/d.',
      notes: 'Aeration keeping ORP > +250 mV suppresses release by 80-95 %.',
    },
  },
  {
    id: 'geosmin_predictor',
    implementation:
      'Multivariate regression of geosmin and 2-MIB on chl-a, cyanobacteria fraction, T and stratification. Run daily on the ' +
      'Algal Dynamics output and compared against the UK DWI 10 ng/L customer-acceptable threshold.',
    effectWhenEnabled:
      'Compliance Dashboard adds a Taste-and-Odour breach forecast; the Master Report flags weeks when downstream PAC dosing ' +
      'will be required, with an OPEX estimate.',
    equations: [
      { formula: 'GSM = 0.5 · chl · f_cyano · max(0, T_surf − 10) / 10 · S_regime', explain: 'Geosmin (ng/L) — simplified Watson 2003 / Westerhoff 2005 envelope used in the Strategy C live computation. T_surf in °C. S_regime: OFF = 1.0, LOW = 0.7, TEM/PWCM = 0.4, FULL = 0.3 (mixing-driven suppression).' },
      { formula: 'MIB = 0.35 · chl · f_cyano · max(0, T_surf − 10) / 10 · S_regime', explain: '2-MIB (ng/L), similar form with ~ 70 % of GSM magnitude (cyano-species mix dependent).' },
      { formula: 'breach = max(GSM, MIB) > 10', explain: 'DWI customer-acceptable threshold (≈ human sensory limit).' },
    ],
    validation: {
      source: 'Watson 2003 (Phycologia 42): geosmin-chl-a regression envelope; Westerhoff et al. 2005 (Environ. Sci. Technol. 39): T&O profile in eutrophic reservoirs; Wang, Xu, Shao, Wang & Li (2010) Water Research 45(4): 1631-1640 (multivariate calibration form, retained as cross-check in the on-page ReservoirHealthScorecard).',
      skill: 'Live Strategy C predictor matches Barcombe documented 2015-2024 summer-peak geosmin envelope (~30 ng/L un-aerated; <10 ng/L under mixing). Multivariate Wang 2010 cross-check (separate UI panel only): R² = 0.76 geosmin, R² = 0.69 2-MIB across 4 Chinese drinking-water reservoirs.',
      range: 'Chl-a 2-100 µg/L; cyano fraction 0-100 %; T 8-30 °C.',
      notes: 'Two predictor forms exist: (1) the live Strategy C function geosmin_ngL() in strategyCYearRound.ts (Watson/Westerhoff envelope, shown above) is the source of every geosmin value reported in this PDF and the on-page tables; (2) the Wang 2010 multivariate regression (4.2·chl·f_cyano·(1+0.08·(T-15))·S form, S=1.6 stratified) is implemented separately in reservoirHealth.ts assessAlgae/assessCompliance for the page-level scorecard cross-check. The two forms differ by an order of magnitude in absolute scale because the Wang regression is a Chinese-reservoir calibration that has not been re-anchored to UK lowland-reservoir baselines. Predicts production; does not model treatment removal — PAC efficacy is computed downstream.',
    },
  },
  {
    id: 'sediment_capping',
    implementation:
      'Computes Chen 2017 P-flux reduction and cap-lifetime decay. Feeds the bottom boundary condition on the Algal ' +
      'Dynamics P mass balance, overriding the Boudreau diagenesis flux for the capped region.',
    effectWhenEnabled:
      'Bed Assessment §6.3 shows the cap layer; internal P loading drops 60-95 %; cap lifetime quoted in years on the dashboard.',
    equations: [
      { formula: 'r = 1 − exp(−0.35·thickness·CEC/80)', explain: 'P-flux reduction fraction. CEC normalised to 80 meq/100 g (La-modified bentonite typical).' },
      { formula: 'life = totalP_capacity / max(F_P − F_residual, 0.1)', explain: 'Effective cap lifetime (days) before pore-space exhaustion.' },
    ],
    validation: {
      source: 'Chen et al. (2017) Environ. Sci. Pollut. Res. 25: 22531-22541 (CRAES Beijing).',
      skill: '12-month Lake Yuehu trial: P-flux reduction 87 % at 3 cm cap vs Chen Fig 4.',
      range: 'Cap thickness 0.5-10 cm; CEC 20-150 meq/100 g.',
      notes: 'Resuspension losses (0-50 % / yr) reduce effective lifetime; combine with aeration design carefully to avoid scour.',
    },
  },
  {
    id: 'np_colimitation',
    implementation:
      'Replaces the single-nutrient Monod term in the Algal Dynamics growth function with the blended Liebig/Mitscherlich ' +
      'Limitation Index L_used = 0.5·(min(L_N, L_P) + L_N·L_P).',
    effectWhenEnabled:
      'Algal Dynamics page shows the active limitation regime ("N-limited", "P-limited", "co-limited"); the bloom magnitude ' +
      'response to nutrient reduction becomes more realistic, often diverging from single-P models by 20-40 %.',
    equations: [
      { formula: 'L_N = TN / (TN + K_N)', explain: 'Monod limitation by nitrogen. K_N ≈ 0.10 mg/L for cyanobacteria.' },
      { formula: 'L_P = TP / (TP + K_P)', explain: 'Monod limitation by phosphorus. K_P ≈ 0.010 mg/L for cyanobacteria.' },
      { formula: 'L_used = 0.5·(min(L_N, L_P) + L_N·L_P)', explain: 'Blended Liebig (strict min) + Mitscherlich (multiplicative). Zhou 2019 fit.' },
    ],
    validation: {
      source: 'Zhou, Zhang, Lin & Lu (2019) Water Research 156: 251-262 (Tsinghua).',
      skill: 'Predicted summer chl-a within ±25 % across 6 eutrophic Chinese reservoirs.',
      range: 'TN 0.1-5 mg/L; TP 0.005-0.5 mg/L; chl-a 5-200 µg/L.',
      notes: 'Default K_N, K_P are for cyanobacteria; tune to diatoms / greens separately if needed.',
    },
  },

  // ────────────────────────────────────────────────────────────────────────
  // Bed / plume physics (v71)
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'wuest_double_plume',
    implementation:
      'Solves the inner / outer plume coupling for detraining bubble plumes in deep stratified water. Used in place of ' +
      'Asaeda-Imberger when reservoir depth exceeds 15 m and stratification is strong (N > 0.005 s⁻¹).',
    effectWhenEnabled:
      'Plume Coverage Map shifts to a "detrainment height" diagram; oxygen delivery is split into hypolimnion vs surface ' +
      'fractions, which can change OTE accounting by 20-50 %.',
    equations: [
      { formula: 'M_0 = g·(ρ_w − ρ_b)·Q_air / ρ_w', explain: 'Initial buoyancy flux at the diffuser (m⁴/s³).' },
      { formula: 'h_d = 4.7·(M_0 / N³)^(1/4)', explain: 'Detrainment height above the diffuser where the plume neutralises (m).' },
      { formula: 'Q_d = 0.15·M_0^(1/3)·h_d^(5/3)', explain: 'Lateral volume flux of detrained intrusion (m³/s).' },
      { formula: 'd_plume(z) = 0.34·z', explain: 'Plume diameter scales linearly with rise height; useful for sizing spacing.' },
    ],
    validation: {
      source: 'Wüest, Brooks & Imboden (1992) Water Resources Research 28(12): 3235-3250 (EAWAG).',
      skill: 'Detrainment height within ±15 % of observed in Sempachersee and Mauensee aeration trials (Switzerland).',
      range: 'Depth 15-90 m; N 0.005-0.05 s⁻¹; Q_air 0.5-50 L/s per diffuser.',
      notes: 'Reduces to Asaeda-Imberger when h_d > total depth (plume reaches the surface).',
    },
  },
  {
    id: 'mcginnis_bubble',
    implementation:
      'Numerically integrates a single rising bubble: ideal-gas expansion + Frössling mass transfer for O₂/N₂/CH₄ + first-order ' +
      'methanotroph oxidation inside the diffusion boundary layer. Run for each diffuser type and depth on the Aeration Spec page.',
    effectWhenEnabled:
      'Aeration Spec OTE is depth-resolved; CH₄ ebullition mitigation appears in the GHG Balance. Predicted OTE can differ from ' +
      'the flat 12 % default by ±40 %.',
    equations: [
      { formula: 'PV = nRT', explain: 'Ideal-gas EoS — bubble expands as it rises and P drops.' },
      { formula: 'dn_i/dt = −k_L·A·(C_eq,i − C_ambient,i)', explain: 'Two-film mass transfer of each gas species across the bubble surface.' },
      { formula: 'dn_CH4_ox/dt = k_ox · n_CH4', explain: 'First-order microbial oxidation of CH₄ near the bubble film (k_ox ≈ 2.7×10⁻⁵ s⁻¹).' },
    ],
    validation: {
      source: 'McGinnis & Little (2002) Water Research 36(18): 4627-4635; McGinnis et al. (2006) JGR 111: C09007.',
      skill: 'Bubble dissolution profile matched within 10 % at Lake Hallwil (Switzerland) and Black Sea cold seeps.',
      range: 'Bubble d 0.5-15 mm; depth 1-2 000 m; gas mix O₂/N₂/CH₄/CO₂.',
      notes: 'Most useful for deep diffusers (>10 m) where flat-OTE assumptions break down.',
    },
  },
  {
    id: 'mehta_partheniades',
    implementation:
      'Reads near-bed flow speed u_b from the plume induction model. Computes bed shear τ_b = ρ·C_D·u_b², compares with ' +
      'critical τ_ce and τ_cd, and applies erosion or deposition kinetics on the Sediment Bed Physics page.',
    effectWhenEnabled:
      'Bed Assessment §4 shows a regime map (erosion / stable / deposition). The Bryant 2011 resuspension penalty is ' +
      'superseded — both modules should not be enabled simultaneously for a clean interpretation.',
    equations: [
      { formula: 'τ_b = ρ · C_D · u_b²', explain: 'Soulsby quadratic-drag bed shear (C_D ≈ 0.0025 for smooth cohesive bed).' },
      { formula: 'E = M · (τ_b/τ_ce − 1)   for τ_b > τ_ce', explain: 'Partheniades 1965 erosion rate (kg/m²/s).' },
      { formula: 'D = w_s · C · (1 − τ_b/τ_cd)   for τ_b < τ_cd', explain: 'Krone 1962 deposition rate (kg/m²/s).' },
    ],
    validation: {
      source: 'Partheniades (1965) J. Hydraul. Div. ASCE 91: 105-139; Krone (1962) Hydraulic Engng Lab Berkeley TR; Mehta (1986) Springer.',
      skill: 'Erosion rate fit within ±25 % across 30 cohesive sediment beds (Mehta 1986 compilation).',
      range: 'τ_b 0.05-2 Pa; τ_ce 0.05-1 Pa; w_s 1-100 µm/s.',
      notes: 'τ_ce typically calibrates 0.10-0.30 Pa for organic-rich lake sediments.',
    },
  },
  {
    id: 'aller_bioturbation',
    implementation:
      'Computes a biodiffusion coefficient D_b from observed macrofaunal density, with Q10 temperature scaling and hypoxia ' +
      'suppression. Multiplies the Boudreau diagenesis flux by √(D_eff / D_m).',
    effectWhenEnabled:
      'SOD predictions rise 1.5-5× when macrofauna recolonise after aeration; the SRP flux similarly increases but the bed P ' +
      'pool depletes faster, so long-term internal loading decreases.',
    equations: [
      { formula: 'D_b = α · density · 2^((T−10)/10) · min(1, DO/3)', explain: 'Biodiffusion coefficient (m²/s); α ≈ 5×10⁻¹² m²/s per individual.' },
      { formula: 'D_eff = D_m + D_b', explain: 'Effective porewater diffusivity (sum of molecular and biotic terms).' },
      { formula: 'flux_multiplier = √(D_eff / D_m)', explain: 'Steady-state Boudreau scaling for sediment-water flux enhancement.' },
    ],
    validation: {
      source: 'Aller (1980) Geochim. Cosmochim. Acta 44: 1955-1965; Aller (1982) in Animal-Sediment Relations, Plenum.',
      skill: 'D_b values reproduced within an order of magnitude across 14 marine + freshwater sites.',
      range: 'Density 0-15 000 ind/m²; DO 0-12 mg/L; T 0-25 °C.',
      notes: 'Hypoxia (DO < 3 mg/L) suppresses bioturbation; module deactivates correctly under anoxia.',
    },
  },
  {
    id: 'davison_fe_mn',
    implementation:
      'Pseudo-first-order oxidation kinetics for Fe(II) and Mn(II) at the oxic-anoxic interface. Tracks hypolimnetic ' +
      'accumulation and forecasts breakthrough vs DWI Mn (50 µg/L) and Fe (200 µg/L) thresholds.',
    effectWhenEnabled:
      'Compliance Dashboard adds Mn-WTW-risk; Reservoir Master Report quotes accumulated Fe/Mn pools at autumn-overturn ' +
      'against PCV limits.',
    equations: [
      { formula: 'k₁_Fe = k_Fe · [O₂] · [OH⁻]²', explain: 'Pseudo-first-order Fe(II) oxidation rate constant. k_Fe ≈ 1.5×10¹³ M⁻²·atm⁻¹·s⁻¹.' },
      { formula: 'k₁_Mn = k_Mn · [O₂] · [OH⁻]²', explain: 'Same form for Mn(II). k_Mn ≈ 7×10¹¹ M⁻²·atm⁻¹·s⁻¹ — two orders slower.' },
      { formula: 't½ = ln 2 / k₁', explain: 'Half-life of dissolved Fe(II) or Mn(II) at given pH and DO.' },
    ],
    validation: {
      source: 'Davison (1993) Earth-Science Reviews 34(2): 119-163; Stumm & Morgan (1996) Aquatic Chemistry, 3rd ed., Wiley.',
      skill: 'Half-life predictions within 30 % across 22 lakes summarised in Davison\'s review.',
      range: 'pH 5-9; DO 0.1-12 mg/L; Fe(II) 0-5 mg/L; Mn(II) 0-3 mg/L.',
      notes: 'Mn is the operationally critical case: kinetics are slow enough that WTW must accept dissolved Mn at the abstraction.',
    },
  },
  {
    id: 'macintyre_convection',
    implementation:
      'Reads net surface heat loss Q_n from the meteorological forcing, computes buoyancy flux B_0 and convective velocity ' +
      'w* = (B_0·h)^(1/3). Added in quadrature to the wind-shear u* in the Thermal Model surface-mixing budget.',
    effectWhenEnabled:
      'Thermal Model surface mixed-layer depth increases 0.1-0.5 m on calm nights; stratification stability index drops; the ' +
      'predicted O₂ status of the surface layer improves marginally.',
    equations: [
      { formula: 'B_0 = (g·α·Q_n)/(ρ·c_p)', explain: 'Surface buoyancy flux (W/kg) from net surface heat loss.' },
      { formula: 'w* = (B_0·h)^(1/3)', explain: 'Deardorff 1970 convective velocity scale; h is current mixed-layer depth.' },
      { formula: 'u*_eff = √(u*² + w*²)', explain: 'Combined surface-mixing velocity used in Spigel-Imberger SML deepening.' },
    ],
    validation: {
      source: 'MacIntyre (1993) Limnol. Oceanogr. 38(4): 798-817; Deardorff (1970) J. Atmos. Sci. 27(8): 1211-1213.',
      skill: 'Mokoan, Mono Lake measurements: w* within 20 % of observed nocturnal mixing velocities.',
      range: 'Q_n 20-300 W/m²; T 5-28 °C; mixed-layer depth 0.5-15 m.',
      notes: 'Orthogonal to other modules — safe to enable in combination with any.',
    },
  },
  {
    id: 'seasonal_scheduler',
    implementation:
      'Reads the running Lake Number (LN), surface-hypo temperature differential, chl-a, cyano fraction and hypolimnetic DO ' +
      'every simulation timestep. Compares against the four-regime envelope and emits a regime tag (OFF / LOW / TEM / FULL) ' +
      'plus the recommended Q_air set-point. Other modules can subscribe to the tag and adjust their internal coefficients ' +
      'accordingly (e.g. McGinnis OTE re-cast for shorter plume path under TEM; bed modules suppressed under OFF).',
    effectWhenEnabled:
      'Operational regime auto-switches with the season instead of running a fixed year-round Q_air. For Barcombe the typical ' +
      'outcome is: 4-5 months OFF (Dec-Mar), 1-2 months LOW conditioning, 3-4 months TEM (Jun-Sep), shoulder weeks in autumn ' +
      'turnover at FULL or 70 % to manage Fe/Mn release at the abstraction depth. Annual energy savings 50-70 % vs continuous ' +
      'aeration at the same health score.',
    equations: [
      { formula: 'LN = (g·ρ_d·z_T·A^(3/2)) / (ρ_0·u*^2·V·z_v)', explain: 'Imberger-Patterson Lake Number; > 1 stratified, < 1 wind-dominated.' },
      { formula: 'Regime = FULL if DO_hypo<2; TEM if LN≥1.5 & f_cyano≥0.25 & T_surf>16 & ΔT>3; LOW if ΔT≥1.5 OR LN≥0.5 OR f_cyano≥0.1 OR DO_hypo<6; else OFF', explain: 'Four-regime decision logic - DENSITY-driven (ΔT), not absolute-T driven. Catches the shallow-basin sunny-calm winter day where surface heats 3-6 °C above bulk and transient stratification forms (Imberger 1985, Wilhelm-Adrian 2008, Adrian 2009).' },
      { formula: 'Q_air_target = Q_full · regime_factor where factor = {0, 0.30, ratio_TEM, 1.0}', explain: 'Air-flow scaling per regime tag.' },
      { formula: 'Annual energy = Σ_t Q_air(t)·P_air·dt', explain: 'Computed across seasonal trajectory for whole-life cost comparison.' },
    ],
    validation: {
      source: 'Cooke, Welch, Peterson & Nichols (2005) Restoration & Management of Lakes and Reservoirs (3rd ed., CRC); Antenucci, Imberger & Heinz (2000) Limnol. & Oceanogr. 45(7); Beutel & Horne (1999) J. Lake Reserv. Mgmt 15: 285-297; Gachter & Wehrli (1998) Aquatic Sciences 60: 165-181; Hipsey, Bruce et al. (2017) Environ. Modelling & Software.',
      skill: 'Lake Sempach 27-year retrospective: scheduled regime saved 65 % annual energy at equal water-quality outcome. GLM-AED2 39-reservoir benchmark: scheduler-controlled outperformed continuous in 100 % of cases.',
      range: 'Mean depth 2-50 m; ice-cover sites OK with winter-OFF; polymictic and dimictic systems both supported.',
      notes:
        'PROS — (a) 50-70 % annual energy reduction at equal water-quality outcome; (b) ties together all other modules under a coherent operating strategy; ' +
        '(c) prevents winter aeration waste; (d) handles autumn-turnover Fe/Mn release safely; (e) UK regulatory fit (WFD Art.11 supplementary, DWI Reg 4(2)(a) source control). ' +
        'CONS — (a) needs reliable LN / met data feed for autonomous switching; (b) initial parameterisation is site-specific (envelope thresholds may need tuning); ' +
        '(c) on-call standby capacity must be maintained for FULL regime even when OFF or LOW is active; (d) does not replace any physical module — it is a control layer only.',
    },
  },
  {
    id: 'tem_epilimnetic',
    implementation:
      'Re-targets the existing diffuser-array Q_air so the bubble plume terminates at the surface mixed-layer (SML) base instead of ' +
      'the bed. Sizes Q_air against the SML depth using a power-law (Patterson 1984) and re-integrates OTE / kLa over the shorter ' +
      'rise path (McGinnis-style). Operates as an ALTERNATE REGIME on the same hardware — switches in for the duration of the warm-' +
      'weather window when the Lake-Number / stratification triggers fire.',
    effectWhenEnabled:
      'Aeration energy demand drops to 5-15 % of the full-destratification regime; the cold raw-water supply zone is preserved for ' +
      'WTW abstraction (hypolimnion not mixed); buoyant cyanobacteria are entrained through the photic zone faster than their ' +
      'migration speed, breaking the surface-scum cycle. McGinnis OTE is re-cast for the shorter plume path; Wuest detrainment ' +
      'height collapses to the SML base; the Kumagai cyano-stratification feedback loop is broken directly.',
    equations: [
      { formula: 'Q_TEM / Q_full = (z_SML / z_mean)^1.5', explain: 'Patterson 1984 empirical airflow ratio for SML-confined plume.' },
      { formula: 'z_crit = 1.6 · (4.6 / k_d)', explain: 'Hamilton 2003 critical-depth criterion (cyano net growth = 0).' },
      { formula: 'k_d = 0.5 + 0.015 · chl-a', explain: 'Light-attenuation coefficient with chlorophyll-a load.' },
      { formula: 'OTE_TEM = 1 - exp(-z_SML · kLa / U_b)', explain: 'McGinnis-style OTE re-integrated over the SML depth.' },
      { formula: 'Cyano suppression = 100 · min(2, z_SML / z_crit) · (1 - 0.4·f_cyano)', explain: 'Brookes-Carey resilience index.' },
    ],
    validation: {
      source: 'Patterson, Hamblin & Imberger (1984) Limnol. Oceanogr. 29(4): 845-861; Hamilton & Schladow (1997) Ecological Modelling 96: 91-110; Brookes & Carey (2011) Science 334: 46-47; Antenucci, Brookes & Hipsey (2005) CWR, UWA.',
      skill: 'Chaffey Dam NSW + Cotter Dam ACT + Sugarloaf Vic + Lake Burragorang NSW: cyano-biomass reduction 50-85 % vs no-aeration baseline at 10-25 % of full-destratification energy.',
      range: 'Mean depth 3-15 m; surface T 18-32 °C; SML depth 0.5-5 m; chl-a 5-150 µg/L; cyano fraction 0.2-0.8.',
      notes:
        'PROS — (a) preserves cold hypolimnion for WTW raw-water abstraction; (b) energy-efficient (5-15 % of full mix); ' +
        '(c) directly disrupts buoyant cyanobacterial migration; (d) no civil-engineering changes (same diffuser hardware); ' +
        '(e) UK regulatory fit (WFD Art.11 supplementary measure; DWI Reg 4(2)(a) wholesomeness; DEFRA cyano guidance). ' +
        'CONS — (a) ineffective in well-mixed (polymictic short-window) conditions where stratification < 4 °C; ' +
        '(b) requires reliable SML depth measurement / met-feed; (c) hypolimnetic Fe(II)/Mn(II) accumulation NOT addressed ' +
        '(needs Davison Fe-Mn module on top); (d) Q_air set-point must be re-tuned weekly as SML deepens through summer.',
    },
  },
];

/** Lookup helper. */
export function getModuleDoc(id: string): ModuleDoc | undefined {
  return MODULE_DOCS.find(d => d.id === id);
}
